/**
 * Document numbering.
 *
 * Two properties must hold at once, and they pull against each other:
 *
 *   UNIQUE across every terminal, including two that have never been online at
 *   the same time (FR-020).
 *
 *   GAP-FREE within a series, including across abandoned carts, crashes, and
 *   voids (FR-019, FR-015, FR-023).
 *
 * Uniqueness is STRUCTURAL: each terminal owns a series, assigned server-side
 * inside a transaction at registration. Two terminals cannot collide because
 * they can never hold the same series — collision is prevented at assignment
 * rather than detected at sync.
 *
 * Gap-freeness is LOCAL and TRANSACTIONAL, and it does not live in this file.
 * The counter advances inside the same IndexedDB transaction that writes the
 * sale (see repositories.ts commitAtomically), so "number allocated but sale
 * not written" is not a state that can exist. Everything here is pure: it
 * computes and validates, and reserves nothing.
 */

/** Uppercase alphanumeric, 2–8 characters. No hyphen — that is the separator. */
const SERIES_PATTERN = /^[A-Z0-9]{2,8}$/;
const SEPARATOR = "-";
const PAD_WIDTH = 6;

export interface DocumentNumber {
  series: string;
  sequence: number;
  documentNumber: string;
}

export interface SeriesValidation {
  ok: boolean;
  reason?: string;
}

export function validateSeries(series: string): SeriesValidation {
  if (!SERIES_PATTERN.test(series)) {
    return {
      ok: false,
      reason: "Series must be 2–8 uppercase letters or digits, with no separator",
    };
  }
  return { ok: true };
}

export class NumberingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NumberingError";
  }
}

/**
 * Zero-padded so numbers sort lexically as well as numerically — which matters
 * because reports and exports order by string more often than anyone intends.
 *
 * Past the padded width the number simply grows. It never wraps: a café that
 * outlives six digits must not start reissuing numbers it has already used.
 */
export function formatDocumentNumber(series: string, sequence: number): string {
  if (!Number.isInteger(sequence) || sequence < 1) {
    throw new NumberingError(`Sequence must be a positive whole number, got ${sequence}`);
  }
  return `${series}${SEPARATOR}${String(sequence).padStart(PAD_WIDTH, "0")}`;
}

/** Returns null rather than guessing. A malformed number is never repaired. */
export function parseDocumentNumber(value: string): { series: string; sequence: number } | null {
  const index = value.indexOf(SEPARATOR);
  if (index <= 0) return null;

  const series = value.slice(0, index);
  const digits = value.slice(index + 1);

  if (!SERIES_PATTERN.test(series)) return null;
  if (!/^\d+$/.test(digits)) return null;

  const sequence = Number.parseInt(digits, 10);
  if (!Number.isSafeInteger(sequence) || sequence < 1) return null;

  return { series, sequence };
}

/** Pure. Reserves nothing — the committing transaction owns the reservation. */
export function nextNumber(series: string, sequence: number): DocumentNumber {
  return {
    series,
    sequence,
    documentNumber: formatDocumentNumber(series, sequence),
  };
}

const MAX_REPORTED_MISSING = 100;

/**
 * Raised when an incoming sale's sequence is not the immediate successor.
 *
 * IMPORTANT: on the sync path this is ACCEPTED AND FLAGGED (HTTP 202), never
 * rejected. A break means something already went wrong; refusing the sale would
 * discard a real transaction that happened in the café and make the books worse,
 * not better. See contracts/sync-api.md SEQUENCE_BREAK.
 */
export class SequenceBreakError extends Error {
  readonly missing: number[];
  readonly missingCount: number;

  constructor(
    readonly series: string,
    readonly expected: number,
    readonly received: number,
  ) {
    super(
      received < expected
        ? `Series ${series}: sequence ${received} replays or regresses; expected ${expected}`
        : `Series ${series}: gap before ${received}; expected ${expected}`,
    );
    this.name = "SequenceBreakError";

    if (received > expected) {
      this.missingCount = received - expected;
      const capped = Math.min(this.missingCount, MAX_REPORTED_MISSING);
      this.missing = Array.from({ length: capped }, (_, i) => expected + i);
    } else {
      this.missingCount = 0;
      this.missing = [];
    }
  }
}

/**
 * Validate that `received` follows `lastSeen` with no gap.
 *
 * `lastSeen` is null when the series has issued nothing yet, in which case the
 * first number must be 1.
 */
export function assertContinuity(
  series: string,
  lastSeen: number | null,
  received: number,
): void {
  const expected = lastSeen === null ? 1 : lastSeen + 1;
  if (received !== expected) {
    throw new SequenceBreakError(series, expected, received);
  }
}
